import { PopImage } from '@ps/ui/atoms/pop-image';
import { cn } from '@ps/ui/utils';

interface Props {
  /** Resolved cover URL — renders nothing when null/empty. */
  src?: string | null;
  alt: string;
  /** Host-controlled sizing/rounding — full-bleed height on the standalone
   *  hero, an aspect-ratio box in the contained creator modal. */
  className?: string;
  priority?: boolean;
}

// The banner's bottom fades to transparent so it dissolves into whatever the
// host paints behind it — the extracted banner color on the standalone hero
// (like the My Life card), the dark surface in the creator modal. A mask (not a
// black gradient overlay) is what makes it blend into a COLOR rather than always
// to black. Inline (not Tailwind) — arbitrary mask/gradient stops don't reliably
// compile here, matching the inline fades used elsewhere in the slice.
const FADE_MASK = 'linear-gradient(to bottom, #000 70%, transparent 100%)';

/**
 * Course cover banner — the image with its bottom masked away so it dissolves
 * into the host's background (1:1 with the My Life series-card banner). Pure +
 * layout-agnostic: the host sizes it via `className` (a fixed hero height full-
 * bleed on the standalone page, an `aspect-[8/3]` box in the modal) and paints
 * the color it fades into. Renders nothing when the course has no banner.
 */
export function CourseBanner({ src, alt, className, priority }: Props) {
  if (!src) {
    return null;
  }
  return (
    <div
      className={cn('relative w-full overflow-hidden', className)}
      style={{ maskImage: FADE_MASK, WebkitMaskImage: FADE_MASK }}
    >
      <PopImage
        src={src}
        alt={alt}
        fill
        resizing={{ width: 1080, height: 480, fit: 'cover' }}
        sizes="100vw"
        resizeMode="cover"
        priority={priority}
      />
    </div>
  );
}
